import { endpoint } from '~/api';
import { ID } from '~/lib/model';
import { IPaginator } from '~/lib/paginator';
import { ILimitedSelfServeSource } from '~/modules/Sources/models/LimitedSelfServeSource';
import {ISource, ISourceSummary, adaptSourceSummary, ISourceTags} from '~/modules/Sources/models/Source';
import { IImportResult, IPlacement } from '~/modules/Sources/models/Placement';

export type ISourcesFilter = {
    active?: boolean | null;
    sourceSearch?: string;
    idSearch?: string;
    sellerSearch?: string;
    tagFilter?: string;
};

export interface ISourcesPaginator {
    pageNo?: number;
    pageSize?: number;
    sortBy?: string;
}

interface IFetchSourcesPageOptions {
    signal?: AbortSignal;
}

const adaptSourcesPaginator = (
    data: IPaginator<unknown>,
    fallback: ISourcesPaginator
): IPaginator<ISourceSummary> => ({
    items: Array.isArray(data?.items) ? data.items.map(adaptSourceSummary) : [],
    count: Number(data?.count) || 0,
    page: data?.page != null ? Number(data.page) : (fallback.pageNo ?? 0),
    pageSize: data?.pageSize != null ? Number(data.pageSize) : (fallback.pageSize ?? 25),
});

export const fetchSourcesPage = (
    filter: ISourcesFilter = {},
    paginator: ISourcesPaginator = {},
    options: IFetchSourcesPageOptions = {}
): Promise<IPaginator<ISourceSummary>> => {
    const params = new URLSearchParams();
    const { active, sourceSearch, idSearch, sellerSearch, tagFilter } = filter;
    const { pageNo, pageSize, sortBy } = paginator;

    if (typeof active === 'boolean') {
        params.set('active', String(active));
    }

    const trimmedName = (sourceSearch ?? '').trim();
    if (trimmedName) params.set('name', trimmedName);

    const trimmedId = (idSearch ?? '').trim();
    if (trimmedId) params.set('id', trimmedId);

    const trimmedSeller = (sellerSearch ?? '').trim();
    if (trimmedSeller) {
        params.set('sellerId', trimmedSeller);
    }

    const trimmedTag = (tagFilter ?? '').trim();
    if (trimmedTag) {
        params.set('tag', trimmedTag);
    }

    if (pageNo != null) params.set('pageNo', String(pageNo));
    if (pageSize != null) params.set('pageSize', String(pageSize));
    if (sortBy != null) params.set('sortBy', sortBy);

    return endpoint
        .get<IPaginator<unknown>>(`/sources-view?${params}`, { signal: options.signal })
        .then(r => adaptSourcesPaginator(r.data, paginator));
};

export const prepareSourceSubmitData = (source: ISource) => {
    let updatedSource = JSON.parse(JSON.stringify(source as any));
    if (updatedSource.priceRounding != null) {
        {
            Object.keys(updatedSource.priceRounding).map(type => {
                updatedSource.priceRounding[type] =
                    updatedSource.priceRounding[type]
                        .replace(/^\s+|\s+$/g, '')
                        .split('\n')
                        .map(parseFloat)
                        .filter(line => line);
            });
        }
    }
    if (updatedSource.scale === '') {
        updatedSource.scale = null;
    }
    if (updatedSource.maxRps === '') {
        updatedSource.maxRps = null;
    }
    return updatedSource;
};

export const fetchSources = (filter: ISourcesFilter = {}) => {
    let url = new URLSearchParams();
    let { active } = filter || {};
    if (typeof active === 'boolean') {
        url.append('enabled', active.toString());
    }
    return endpoint.get<ISource[]>('/sources?' + url.toString()).then(r => r.data);
};
export const fetchSource = (sourceId: ID) => endpoint.get<ISource>(`/sources/${sourceId}`).then(r => r.data);
export const deleteSource = (source: ISource) => endpoint.delete(`/sources/${source.id}`).then(r => r.data);
export const createSource = (source: ISource) => endpoint.post('/sources', source).then(r => r.data);
export const updateSource = (source: ISource) => endpoint.put(`/sources/${source.id}`, source).then(r => r.data);
export const updateLimitedSource = (source: ILimitedSelfServeSource) => endpoint.put(`/sources/limited/${source.id}`, source).then(r => r.data);
export const toggleSource = (source: ISource, active: boolean) => endpoint.request({
    method: active ? 'POST' : 'DELETE',
    url: `/sources/${source.id}/active`
}).then(() => ({ ...source, active }));
export const updateSourcesTags = (tags: ISourceTags[]) => endpoint.patch('/sourceTags', tags).then(r => r.data);

export interface IPlacementsQuery {
    pageNo?: number;
    pageSize?: number;
    q?: string;
    bundle?: string;
    platform?: string;
    adType?: string;
    placementType?: string;
    sortBy?: string;
}

export const fetchPlacementsPage = (
    sourceId: ID,
    query: IPlacementsQuery = {},
    options: { signal?: AbortSignal } = {}
): Promise<IPaginator<IPlacement>> => {
    const params = new URLSearchParams();
    const { pageNo, pageSize, q, bundle, platform, adType, placementType, sortBy } = query;

    const trimmedQ = (q ?? '').trim();
    if (trimmedQ) params.set('q', trimmedQ);

    const trimmedBundle = (bundle ?? '').trim();
    if (trimmedBundle) params.set('bundle', trimmedBundle);

    if (platform) params.set('platform', platform);
    if (adType) params.set('adType', adType);
    if (placementType) params.set('placementType', placementType);

    if (pageNo != null) params.set('pageNo', String(pageNo));
    if (pageSize != null) params.set('pageSize', String(pageSize));
    if (sortBy != null) params.set('sortBy', sortBy);

    return endpoint
        .get<IPaginator<IPlacement>>(`/placement/${sourceId}?${params}`, { signal: options.signal })
        .then(res => res.data);
};

export const createPlacement = (sourceId: ID, placement: Omit<IPlacement, 'id' | 'placementId'>) =>
    endpoint.post<IPlacement>(`/placement/${sourceId}`, placement).then(res => res.data);

export const updatePlacement = (sourceId: ID, placement: IPlacement) =>
    endpoint.put<IPlacement>(`/placement/${sourceId}`, placement).then(res => res.data);

export const deleteSellerPlacement = (id: ID, sourceId: ID) =>
    endpoint.delete(`/placement/${id}/source/${sourceId}`);

export const importPlacements = (file: File, sellerId?: ID) => {
    const data = new FormData();
    data.append('file', file);
    const url = sellerId != null ? `/sellers/${sellerId}/placements/import` : '/publisher/placements/import';
    return endpoint.post<IImportResult>(url, data).then(res => res.data);
};
